import React, {useEffect, useMemo} from 'react';

import assign from 'lodash/assign';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import moment from 'moment';
import RRule, {ByWeekday, Frequency as FreqType, Options} from 'rrule';

import {ShowNonFieldFormErrors} from 'app/general/forms';
import {rruleFromString} from 'app/tasks/utils';
import {gt, gts, ngt} from 'app/utils/i18n';

import {CheckboxWithSubFields} from './CheckboxWithSubFields';
import {MonthSelectFields} from './MonthSelectFields';
import {RRuleRepetitionLabel} from './RRuleRepetitionLabel';
import {
  normalizeByWeekday,
  WEEKDAYS,
  WeekdaysCheckboxes,
} from './WeekdaysCheckboxes';

interface Props {
  dtstart: string;
  rrule: string;
  onChange: (rrule: string) => void;
}

const WORKDAYS: Array<number> = [
  RRule.MO,
  RRule.TU,
  RRule.WE,
  RRule.TH,
  RRule.FR,
].map((v) => v.weekday);

export const RRuleForm = React.memo(function RRuleForm(
  this: void,
  {dtstart, rrule: rruleStr, onChange}: Props
): JSX.Element {
  const rrule = useMemo(() => {
    return normalizeRRule(rruleFromString(rruleStr), dtstart, true);
  }, [rruleStr, dtstart]);

  const {options} = rrule;

  const showWeekdaysFields = useMemo(() => {
    return (
      (options.freq === RRule.DAILY && options.count !== 1) ||
      options.freq === RRule.WEEKLY
    );
  }, [options.freq, options.count]);

  const isRunningForAllWorkdays = useMemo(() => {
    const byweekday = normalizeByWeekday(options.byweekday).map(
      (d) => d.weekday
    );
    return WORKDAYS.every((day) => byweekday.includes(day));
  }, [options.byweekday]);

  function getLocalisedRepeatOptions(
    freqtype: FreqType | undefined,
    n: number | undefined = 1
  ): string | undefined {
    if (freqtype == null) {
      return undefined;
    }

    return {
      [RRule.DAILY]: ngt('day', 'days', n),
      [RRule.WEEKLY]: ngt('week', 'weeks', n),
      [RRule.MONTHLY]: ngt('month', 'months', n),
      [RRule.YEARLY]: ngt('year', 'years', n),
    }[freqtype];
  }

  function updateRRule(options: Partial<Options>, merge = true): void {
    const updatedRRule = normalizeRRule(
      new RRule({
        ...(merge ? rrule.origOptions : {}),
        ...options,
      }),
      dtstart,
      // remove dtstart we set in normalizeRRule,
      // it was required to render the first occurrences
      false
    );

    onChange(updatedRRule.toString());
  }

  useEffect(() => {
    // we use dtstart to validate and normalize the byweekday value,
    // so when dtstart changes, we send the updated rrule to the parent component
    // as byweekday could be updated
    updateRRule({});
  }, [dtstart]);

  /* updates RRULE frequency */
  function updateRRuleFreq(freqString: string) {
    if (freqString === '') {
      // if it's a once only task, remove all aditional options
      return updateRRule(
        {
          freq: RRule.DAILY,
          count: 1,
        },
        false
      );
    } else {
      return updateRRule({
        freq: parseInt(freqString),
        interval: options.interval ?? 1,
        count: null,
        byweekday: [],
      });
    }
  }

  return (
    <div>
      <div className="form-row">
        <div className="form-group col-md-4">
          <label>{gt('Repeat')}</label>
          <select
            className="form-control"
            value={options.count === 1 ? '' : options.freq ?? ''}
            onChange={(e) => updateRRuleFreq(e.target.value)}
          >
            <option value="">{gts('Once Only')}</option>
            <option value={RRule.DAILY}>{gts('Daily')}</option>
            <option value={RRule.WEEKLY}>{gts('Weekly')}</option>
            <option value={RRule.MONTHLY}>{gts('Monthly')}</option>
            <option value={RRule.YEARLY}>{gts('Yearly')}</option>
          </select>
        </div>
        {options.count !== 1 ? (
          <div className="form-group col-md-8">
            <label>{gt('Repeats every')}</label>
            <div className="form-inline">
              <input
                className="form-control col-3 mr-2"
                type="number"
                value={options.interval}
                onChange={(e) =>
                  updateRRule({
                    interval: Math.max(parseInt(e.target.value || '1'), 1),
                  })
                }
                placeholder="1"
              />
              <label>
                {getLocalisedRepeatOptions(options.freq, options.interval)}
              </label>
            </div>
          </div>
        ) : null}
      </div>

      <ShowNonFieldFormErrors errorSelector={(errors) => errors.rrule} />

      {showWeekdaysFields ? (
        <CheckboxWithSubFields
          label={gt('Weekdays only')}
          fieldId="workday-checkbox"
          checked={isRunningForAllWorkdays}
          onChange={(checked) =>
            updateRRule({
              byweekday: selectWorkdays(rrule, checked),
              interval: 1,
              freq: RRule.WEEKLY,
            })
          }
          opened={options.freq === RRule.WEEKLY}
        >
          <WeekdaysCheckboxes
            dueDate={dtstart}
            byweekday={options.byweekday}
            onChange={(byweekday) => updateRRule({byweekday})}
          />
        </CheckboxWithSubFields>
      ) : null}

      {options.freq === RRule.MONTHLY ? (
        <CheckboxWithSubFields
          label={gt('Repeats by day of week')}
          fieldId="repeat-by-day-of-week-checkbox"
          checked={isRepeatsByWeekdayEnabled(options)}
          onChange={(checked) =>
            updateRRule(updateRRuleMonthByWeek(checked, options, dtstart))
          }
          opened={isRepeatsByWeekdayEnabled(options)}
        >
          <WeekdaysCheckboxes
            dueDate={dtstart}
            byweekday={options.byweekday}
            onChange={(byweekday) => updateRRule({byweekday})}
          />
        </CheckboxWithSubFields>
      ) : null}

      {options.freq !== RRule.YEARLY && options.count !== 1 ? (
        <MonthSelectFields options={options} onChange={updateRRule} />
      ) : null}

      <RRuleRepetitionLabel rrule={rrule} />
    </div>
  );
});

// here we do all required processing in the rrule object being created
function normalizeRRule(
  this: void,
  rrule: RRule,
  duedate: string,
  setDtstart: boolean
): RRule {
  let options: Partial<Options> = {...rrule.origOptions};

  const momentDuedate = moment(duedate);

  // when normalizing the rrule for internal use, we set dtstart to render
  // first occurences. When normalizing to send to the parent component the dtstart
  // field is removed. Maybe in the future we can keep it so backend will use it.
  options.dtstart = setDtstart ? momentDuedate.toDate() : undefined;

  if (options.freq === RRule.WEEKLY) {
    options.byweekday = ensureWeekDayIsValid(options, duedate);
  }

  if (options.freq === RRule.MONTHLY) {
    if (isRepeatsByWeekdayEnabled(options)) {
      options.bymonthday = null;
      if (options.byweekday == null) {
        assign(options, getRRuleMonthByWeekData(duedate));
      } else {
        options.bysetpos = getRRuleMonthByWeekData(duedate).bysetpos;
      }
    } else {
      options.byweekday = null;
      options.bymonthday = [momentDuedate.date(), -1];
      options.bysetpos = 1;
    }
  }

  if (options.freq === RRule.YEARLY) {
    options.bymonthday = [momentDuedate.date(), -1];
    options.bymonth = momentDuedate.month() + 1;
    options.bysetpos = 1;
  }

  options.bymonth = ensureMonthIsValid(options.bymonth, duedate);

  options = pickBy(options, (v) => v !== null && v !== undefined);

  // let's exclude all the options we don't care about.
  options = pick(options, getValidOptionsForFreq(options.freq));

  return new RRule(options);
}

function ensureWeekDayIsValid(
  this: void,
  options: Partial<Options>,
  dtstart: string
): Array<ByWeekday> | ByWeekday | null | undefined {
  // since the API don't trigger errors when assigning a weekly task
  // with no weekday defined, we will not allow the user to deselect
  // all weekdays. When they try to deselect the last weekday, the task.due
  // week day will be selected

  const {freq, byweekday} = options;

  if (freq === RRule.WEEKLY) {
    const dtstartWeekday = moment(dtstart).isoWeekday() - 1;
    const normalizedByWeekdays = normalizeByWeekday(byweekday);

    // be careful, the RRule API accepts an array of integers as byweekday value
    // but that will cause the RRule text description to not the use "weekdays" and
    // list all workdays instead. So always provide an array of Weekday instances as byweekday
    // to avoid that bug

    if (normalizedByWeekdays.length === 1) {
      // if a single week day is selected, make sure that's the same as the due date
      return [WEEKDAYS[dtstartWeekday]];
    }
    if (
      // if more than one week day is selected, ensure the due date weekday is in the selection
      normalizedByWeekdays.find((d) => d.weekday === dtstartWeekday) == null
    ) {
      return [...normalizedByWeekdays, WEEKDAYS[dtstartWeekday]];
    }

    return normalizedByWeekdays;
  }
  return byweekday;
}

function ensureMonthIsValid(
  this: void,
  bymonth: Options['bymonth'] | undefined,
  dtstart: string
): Options['bymonth'] {
  const dtstartMonth = moment(dtstart).month() + 1;

  // null means all months, all good
  if (bymonth == null) {
    return null;
  }
  // ensure it's an array
  if (!Array.isArray(bymonth)) {
    bymonth = [bymonth];
  }

  if (bymonth.length === 0) {
    // zero items means all months, all good
    return bymonth;
  }

  if (bymonth.length === 1) {
    // if a single month is selected, make sure that's the same as the due date
    return [dtstartMonth];
  }

  if (bymonth.indexOf(dtstartMonth) == -1) {
    // if more than one month is selected, ensure the due date weekday is in the selection
    return [...bymonth, dtstartMonth];
  }
  return bymonth.sort((a, b) => a - b);
}

function isRepeatsByWeekdayEnabled(opts: Partial<Options>): boolean {
  return Boolean(Boolean(opts.bysetpos) && opts.byweekday && opts.byweekday);
}

function selectWorkdays({options}: RRule, add: boolean): Array<ByWeekday> {
  const byweekday = normalizeByWeekday(options.byweekday).map((d) => d.weekday);

  return add
    ? WORKDAYS.reduce((byweekday, day) => {
        if (byweekday.indexOf(day) === -1) {
          return [...byweekday, day];
        }
        return byweekday;
      }, byweekday)
    : byweekday.filter((d) => WORKDAYS.indexOf(d) == -1);
}

function updateRRuleMonthByWeek(
  enabled: boolean,
  options: Options,
  dtstart: string
): Partial<Options> {
  let update: Partial<Options> = {};

  if (!enabled) {
    update = {
      byweekday: undefined,
      bysetpos: null,
    };

    // since this function is executed on each task.dtstart change
    // we need keep the byweekday if it's a weekly repetition
    if (options.freq !== RRule.WEEKLY) {
      update['byweekday'] = undefined;
    }

    return update;
  }

  return getRRuleMonthByWeekData(dtstart);
}

function getRRuleMonthByWeekData(dtstart: string): Partial<Options> {
  const m = moment(dtstart);
  const weekDay = m.isoWeekday() - 1;
  const monthDay = m.date();
  const monthLastDay = m.endOf('month').date();
  let weekInMonth = Math.ceil(monthDay / 7);

  if (monthDay > monthLastDay - 7)
    // user wants pick a weekday of the last week
    weekInMonth = -1;
  return {
    byweekday: [weekDay],
    bysetpos: weekInMonth,
  };
}

function getValidOptionsForFreq(freq: FreqType | undefined): Array<string> {
  if (freq == null) {
    return [];
  }

  const COMMONS = ['freq', 'interval', 'bymonth', 'dtstart'];

  return {
    [RRule.DAILY]: COMMONS.concat(['count']),
    [RRule.WEEKLY]: COMMONS.concat(['byweekday']),
    [RRule.MONTHLY]: COMMONS.concat(['byweekday', 'bymonthday', 'bysetpos']),
    [RRule.YEARLY]: COMMONS.concat(['bymonthday', 'bysetpos']),
  }[freq];
}
